import React, { useCallback, useState } from 'react';
import { z } from 'zod';
import { ValidatedTextInput } from '../components/ValidatedTextInput';
import Modal from '../utils/Modal';

export const CustomShapeNameModal = ({
  initialValue,
  onClose,
  kind,
}: {
  onClose: (newName: string | null) => void;
  initialValue: string;
  kind: 'create' | 'rename';
}) => {
  const [newShapeName, setNewShapeName] = useState<string | null>(null);

  // If we don't listen to the header close event then modal won't show
  // again after being closed by clicking on the X in the header, because
  // React hasn't been told that the modal closed.
  const modalHeaderRef = useCallback(
    header =>
      header?.addEventListener('aha-modal-header:close', () => {
        onClose(null);
      }),
    [onClose]
  );

  // If we don't listen to the cancelled event the backdrop won't disappear
  // when the user presses escape to cancel the modal (doesn't happen in whiteboard
  // demo app but does in Aha app)
  const modalRef = useCallback(
    modal =>
      modal?.addEventListener('aha-modal:cancelled', () => {
        onClose(null);
      }),
    [onClose]
  );

  return (
    <Modal onClose={() => onClose(null)}>
      <aha-modal open={true} size='small' ref={modalRef}>
        <aha-modal-header
          onClick={e => {
            /* If we don't stop propagation then the click is treated as a click
            outside of the object menu, which closes it. Thus clicking on the X to
            cancel the modal would also close the object menu, which we don't want.
            */
            e.stopPropagation();
          }}
          ref={modalHeaderRef}
        >
          {kind === 'create' ? 'Create custom shape' : 'Rename custom shape'}
        </aha-modal-header>
        <aha-modal-body>
          <div>
            <form
              onSubmit={e => {
                e.preventDefault();
                if (newShapeName) {
                  onClose(newShapeName);
                }
              }}
            >
              <aha-field autofocus inline-form-field>
                <div slot='label'>Name</div>
                <ValidatedTextInput
                  type='text'
                  data-testid='custom-shape-name-field'
                  initialValue={initialValue}
                  zodValidationSchema={z
                    .string()
                    .min(1, { message: 'Please enter a name' })}
                  onValidation={({ value, error }) => {
                    if (error !== null) {
                      setNewShapeName(null);
                    } else {
                      setNewShapeName(value);
                    }
                  }}
                  autoComplete='off'
                  autoFocus
                />
              </aha-field>
            </form>
          </div>
        </aha-modal-body>
        <aha-modal-footer>
          <aha-button
            slot='secondary_actions'
            onClick={e => {
              // Stop propagation otherwise the click event ends up selecting the last shape
              // to insert from the object menu. Interacting with the modal should not
              // affect anything outside of the modal.
              e.stopPropagation();
              onClose(null);
            }}
          >
            Cancel
          </aha-button>
          <aha-button
            kind='primary'
            disabled={newShapeName === null ? true : undefined}
            onClick={e => {
              // Stop propagation otherwise the click event ends up selecting the last shape
              // to insert from the object menu. Interacting with the modal should not
              // affect anything outside of the modal.
              e.stopPropagation();
              if (!newShapeName) {
                console.error(
                  'Should not be able to submit if new custom shape name is not set'
                );
              } else {
                onClose(newShapeName);
              }
            }}
          >
            {kind === 'create' ? 'Create' : 'Rename'}
          </aha-button>
        </aha-modal-footer>
      </aha-modal>
    </Modal>
  );
};
